import { Injectable } from '@angular/core';

export type PryProApp = 'pryspace' | 'pryops';

export interface PryProRuntimeConfig {
  /** Optional absolute API origin. Empty means same-origin `/v1` requests. */
  apiBaseUrl?: string;
  /** Absolute origins used for the cross-frontend handoff flow. */
  pryspaceOrigin?: string;
  pryopsOrigin?: string;
}

declare global {
  interface Window {
    __PRYPRO_CONFIG__?: PryProRuntimeConfig;
  }
}

function runtimeConfig(): PryProRuntimeConfig {
  if (typeof window === 'undefined') return {};
  return window.__PRYPRO_CONFIG__ ?? {};
}

function normalizeOrigin(value: string): string {
  return value.trim().replace(/\/+$/, '');
}

@Injectable({ providedIn: 'root' })
export class ApiUrlService {
  /**
   * Runtime configuration is injected through `assets/runtime-config.js`.
   * Keeping the fallback same-origin lets the production reverse proxy own
   * routing and avoids baking local or production hosts into the bundle.
   */
  readonly base = normalizeOrigin(
    runtimeConfig().apiBaseUrl ??
      (typeof window !== 'undefined' ? window.location.origin : ''),
  );

  url(path: string): string {
    const clean = path.startsWith('/') ? path : `/${path}`;
    return `${this.base}${clean}`;
  }

  websocket(path: string): string {
    return this.url(path).replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  }

  originFor(app: PryProApp): string | null {
    if (typeof window !== 'undefined' && app === 'pryspace') {
      // PrySpace can always identify its own origin without configuration.
      return normalizeOrigin(window.location.origin);
    }
    const config = runtimeConfig();
    const origin = app === 'pryspace' ? config.pryspaceOrigin : config.pryopsOrigin;
    return origin ? normalizeOrigin(origin) : null;
  }

  handoffRedirectUri(targetApp: PryProApp): string | null {
    const origin = this.originFor(targetApp);
    return origin ? `${origin}/handoff` : null;
  }
}
